import {useEffect, useState} from 'react';

import {INFRA_SESSION_STATUS_PATH, infraConsoleHref} from './infraProducts';

export type InfraSessionStatus = 'pending' | 'signed-in' | 'signed-out';

/**
 * Whether the viewer has a cloud console session. The console answers
 * `/session-status` with 200 when its cookie is present; anything else,
 * including a network failure, reads as signed out. Re-checked each time
 * `active` turns on, so a login in another tab shows up on the next open.
 */
export function useInfraSession(active: boolean): InfraSessionStatus {
  const [status, setStatus] = useState<InfraSessionStatus>('pending');
  const [wasActive, setWasActive] = useState(active);
  if (active !== wasActive) {
    setWasActive(active);
    if (active) {
      setStatus('pending');
    }
  }

  useEffect(() => {
    if (!active) {
      return;
    }
    const controller = new AbortController();
    fetch(infraConsoleHref(INFRA_SESSION_STATUS_PATH), {
      credentials: 'include',
      signal: controller.signal,
    })
      .then(response => {
        setStatus(response.ok ? 'signed-in' : 'signed-out');
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setStatus('signed-out');
        }
      });
    return () => controller.abort();
  }, [active]);

  return status;
}
